/**
 * Usage event query operations - Create
 *
 * Persists per-step token & cost telemetry to meridian_usage_events. One row
 * per model request (AI SDK "step"). Inserts are best-effort: a failure here
 * must never break the chat turn, so the caller wraps this in try/catch.
 *
 * The table is the ledger for ALL AI spend, not just conversational Meridian —
 * `surface` says which kind of caller produced the row, and single-shot feature
 * calls (ticket summaries, form-assist, captions, …) carry a NULL `chat_id`
 * because they have no conversation to point at.
 */

import { v4 as uuidv4 } from 'uuid';
import { getSupabaseClient } from '../../supabase-client.js';
import { logger } from '../../../logger.js';

/**
 * One per-step usage row to insert. Token/cost fields come straight from
 * `computeCost` in @west-tennessee-consulting/logger so logs and DB stay consistent.
 */
export interface UsageEventInsert {
  /**
   * The conversation this step belongs to. NULL for surfaces that have none
   * (a single-shot summary or extraction). It is a FK to meridian_chats, so a
   * synthetic placeholder is not an option — it would fail the constraint.
   */
  chat_id?: string | null;
  /**
   * Which kind of caller produced this row. Omit for conversational Meridian
   * (the column defaults to 'meridian-chat'); single-shot feature calls pass
   * 'hub-aux'. This is what lets the long-standing v_meridian_daily_cost view
   * keep its exact historical meaning while new surfaces land in the same table.
   *
   * A 'meridian-chat' row with no chat_id is rejected by a CHECK constraint —
   * the likeliest cause is a caller that set chat_id to null and forgot to set
   * surface, which would otherwise file feature spend under chat.
   */
  surface?: string;
  /** Fine-grained attribution within a surface, e.g. 'ticket-summary'. */
  feature?: string | null;
  user_id?: string | null;
  provider?: string | null;
  model_id: string;
  canonical_model_id?: string | null;
  step_index: number;
  tools: string[];
  finish_reason?: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_write_cost_usd: number;
  cache_read_cost_usd: number;
  total_cost_usd: number;
  pricing_unknown: boolean;
  duration_ms?: number | null;
}

/**
 * Batch-insert per-step usage events for one turn. No-op on an empty array.
 * Throws on DB error so the caller can log it — but the caller MUST treat a
 * failure as non-fatal (telemetry, not chat state).
 */
export async function createUsageEvents(events: UsageEventInsert[]): Promise<void> {
  if (events.length === 0) return;

  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    const rows = events.map((e) => ({
      id: uuidv4(),
      chat_id: e.chat_id ?? null,
      surface: e.surface ?? 'meridian-chat',
      feature: e.feature ?? null,
      user_id: e.user_id ?? null,
      provider: e.provider ?? null,
      model_id: e.model_id,
      canonical_model_id: e.canonical_model_id ?? null,
      step_index: e.step_index,
      tools: e.tools ?? [],
      finish_reason: e.finish_reason ?? null,
      input_tokens: e.input_tokens,
      output_tokens: e.output_tokens,
      cache_creation_input_tokens: e.cache_creation_input_tokens,
      cache_read_input_tokens: e.cache_read_input_tokens,
      total_tokens: e.total_tokens,
      input_cost_usd: e.input_cost_usd,
      output_cost_usd: e.output_cost_usd,
      cache_write_cost_usd: e.cache_write_cost_usd,
      cache_read_cost_usd: e.cache_read_cost_usd,
      total_cost_usd: e.total_cost_usd,
      pricing_unknown: e.pricing_unknown,
      duration_ms: e.duration_ms ?? null,
      created_at: now,
    }));

    const { error } = await client.from('meridian_usage_events').insert(rows);
    if (error) {
      throw error;
    }
  } catch (error) {
    logger.error('error creating usage events', error, {
      op: 'createUsageEvents',
      count: events.length,
    });
    throw error;
  }
}
